"use client";

import dynamic from "next/dynamic";
import { useEffect, useState } from "react";
import { useAuthModal } from "@/modules/auth/stores/useAuthModal";
import { useCartSync } from "@/modules/cart/hooks/useCartSync";
import { useCartStore } from "@/modules/cart/stores/useCartStore";
import { useBreadcrumbCategoriesModal } from "@/modules/categories/stores/useBreadcrumbCategoriesModal";
import { useHeaderCategoryPanel } from "@/modules/categories/stores/useHeaderCategoryPanel";
import { useSearchFullscreen } from "@/modules/search/stores/useSearchFullscreen";
import { StoreSwitcherModal } from "@/modules/store/components/StoreSwitcherModal";

// Imports por ARQUIVO, nunca pelo index do módulo: o barrel arrastaria o
// módulo inteiro de volta para o bundle de toda página.
const loadAuthModal = () => import("@/modules/auth/components/AuthModal");
const loadCartDrawer = () => import("@/modules/cart/components/CartDrawer");
const loadBreadcrumbCategoriesModal = () =>
    import("@/modules/categories/components/page/BreadcrumbCategoriesModal");
const loadHeaderCategoryPanelModal = () =>
    import("@/modules/categories/components/panel/HeaderCategoryPanelModal");
const loadSearchFullscreenModal = () => import("@/modules/search/components/SearchFullscreenModal");
const loadFloatingDockChat = () => import("@/modules/chat/components/dock/FloatingDockChat");

const AuthModal = dynamic(() => loadAuthModal().then((m) => m.AuthModal), { ssr: false });
const CartDrawer = dynamic(() => loadCartDrawer().then((m) => m.CartDrawer), { ssr: false });
const BreadcrumbCategoriesModal = dynamic(
    () => loadBreadcrumbCategoriesModal().then((m) => m.BreadcrumbCategoriesModal),
    { ssr: false }
);
const HeaderCategoryPanelModal = dynamic(
    () => loadHeaderCategoryPanelModal().then((m) => m.HeaderCategoryPanelModal),
    { ssr: false }
);
const SearchFullscreenModal = dynamic(
    () => loadSearchFullscreenModal().then((m) => m.SearchFullscreenModal),
    { ssr: false }
);
const FloatingDockChat = dynamic(() => loadFloatingDockChat().then((m) => m.FloatingDockChat), {
    ssr: false,
});

/** Overlays que só montam ao abrir — baixados na primeira interação. */
const OVERLAY_LOADERS = [
    loadAuthModal,
    loadCartDrawer,
    loadBreadcrumbCategoriesModal,
    loadHeaderCategoryPanelModal,
    loadSearchFullscreenModal,
];

/**
 * Vira true na primeira abertura e não volta: depois de aberto o overlay segue
 * montado, preservando a animação de saída e o estado interno entre aberturas.
 */
function useOpenedOnce(open: boolean): boolean {
    const [opened, setOpened] = useState(open);
    if (open && !opened) setOpened(true);
    return opened;
}

/**
 * Baixa os chunks dos overlays no primeiro toque/tecla do usuário, em segundo
 * plano: quando o clique que abre um deles termina, o código já chegou. Uma
 * visita sem interação (ex.: o PageSpeed) não baixa nada disso.
 */
function usePrefetchOnFirstInteraction(): void {
    useEffect(() => {
        const events = ["pointerdown", "keydown", "touchstart"] as const;
        const prefetch = () => {
            for (const event of events) window.removeEventListener(event, prefetch, true);
            for (const load of OVERLAY_LOADERS) void load();
        };
        for (const event of events) {
            window.addEventListener(event, prefetch, { capture: true, passive: true });
        }
        return () => {
            for (const event of events) window.removeEventListener(event, prefetch, true);
        };
    }, []);
}

/**
 * O chat tem gatilho sempre visível, então não dá para esperar a abertura:
 * ele monta quando o navegador fica ocioso depois do `load`, fora do caminho
 * do primeiro paint e da hidratação. O botão é `fixed` — aparecer depois não
 * desloca a página.
 */
function useAfterIdle(): boolean {
    const [idle, setIdle] = useState(false);

    useEffect(() => {
        let cancel = () => {};
        const schedule = () => {
            if (typeof window.requestIdleCallback === "function") {
                const id = window.requestIdleCallback(() => setIdle(true), { timeout: 3000 });
                cancel = () => window.cancelIdleCallback(id);
            } else {
                // Safari: sem requestIdleCallback.
                const id = window.setTimeout(() => setIdle(true), 1500);
                cancel = () => window.clearTimeout(id);
            }
        };

        if (document.readyState === "complete") {
            schedule();
        } else {
            window.addEventListener("load", schedule, { once: true });
        }
        return () => {
            window.removeEventListener("load", schedule);
            cancel();
        };
    }, []);

    return idle;
}

/**
 * Overlays globais — modais, drawers e docks que qualquer página pode abrir.
 *
 * Só o estado de abertura (stores zustand, poucos bytes) fica no bundle de
 * toda página; o código de cada overlay é baixado sob demanda. Exceção: o
 * seletor de loja, que é pequeno e abre sozinho no primeiro acesso — carregá-lo
 * sob demanda só atrasaria esse modal obrigatório.
 */
export function GlobalOverlays() {
    // Era o CartDrawer quem chamava, sempre montado. É o caminho de
    // recuperação do carrinho (ver useCartSync) e não pode depender do drawer
    // ter sido aberto.
    useCartSync();
    usePrefetchOnFirstInteraction();

    const authOpened = useOpenedOnce(useAuthModal((s) => s.open));
    const cartOpened = useOpenedOnce(useCartStore((s) => s.isOpen));
    const breadcrumbOpened = useOpenedOnce(useBreadcrumbCategoriesModal((s) => s.open));
    const categoryPanelOpened = useOpenedOnce(useHeaderCategoryPanel((s) => s.open));
    const searchOpened = useOpenedOnce(useSearchFullscreen((s) => s.open));
    const chatReady = useAfterIdle();

    return (
        <>
            {authOpened && <AuthModal />}
            <StoreSwitcherModal />
            {cartOpened && <CartDrawer />}
            {breadcrumbOpened && <BreadcrumbCategoriesModal />}
            {categoryPanelOpened && <HeaderCategoryPanelModal />}
            {searchOpened && <SearchFullscreenModal />}
            {chatReady && <FloatingDockChat />}
        </>
    );
}
